import { useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  Flex,
  Text,
  Switch,
  SelectField,
  TextInputField,
  Banner,
  Box,
  spaces,
  Icon,
  Chip,
  useThemeColors,
  ColorsV2,
  sizes,
} from '@electricjs/arc';

import { PaginatedResponse } from '@/types/common';
import { MicrosoftTenant } from '@/types/organizations';

import { useStorefront } from '../../StorefrontProvider';
import { ConfigurationDetailPane } from '../ConfigurationDetailPane';
import { MicrosoftTenantOption } from '../StorefrontCheckoutFormProvider';

type WAutopilotCartSwitchProps = {
  microsoftTenants: PaginatedResponse<MicrosoftTenant>;
};

const WAutopilotSubFormContainer = styled(Box)`
  padding: ${spaces.space_m_rem} ${spaces.space_m_rem} 0 ${spaces.space_m_rem};
  margin-left: ${spaces.space_m_rem};
  background-color: ${({ theme }) => theme.colors[ColorsV2.GRAY_LIGHTEST]};
  border: 1px solid ${({ theme }) => theme.colors[ColorsV2.GRAY]};
  border-radius: ${sizes.size_4};
`;

const TwoColumnGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: ${spaces.space_s_rem};
`;

export const WAutopilotCartSwitch = ({
  microsoftTenants,
}: WAutopilotCartSwitchProps) => {
  const { t } = useTranslation('storefront');
  const { storefrontCheckoutForm, canConfigureWinAutopilot } = useStorefront();

  const wAutopilotEnabled = storefrontCheckoutForm?.watch('wAutopilotEnabled');
  const windowsZtpEnabled = storefrontCheckoutForm?.watch('windowsZtpEnabled');

  const isDisabled = !!windowsZtpEnabled;

  const [chipTextColor, chipBgColor] = useThemeColors([
    ColorsV2.GRAY_DARK,
    ColorsV2.GRAY_LIGHTEST,
  ]);

  // The Windows Autopilot switch is turned on by default.
  useEffect(() => {
    if (wAutopilotEnabled === undefined && canConfigureWinAutopilot) {
      storefrontCheckoutForm?.setValue('wAutopilotEnabled', true);
    }
  }, [storefrontCheckoutForm, wAutopilotEnabled, canConfigureWinAutopilot]);

  const handleToggle = () => {
    const newValue = !wAutopilotEnabled;
    storefrontCheckoutForm?.setValue('wAutopilotEnabled', newValue);
    if (newValue) {
      // mutual exclusion: turn off ZTP
      storefrontCheckoutForm?.setValue('windowsZtpEnabled', false);
    } else {
      // clear sub-form values (and any pending validation error) when toggled off
      storefrontCheckoutForm?.setValue('wAutopilotMsTenantId', undefined);
      storefrontCheckoutForm?.setValue('wAutopilotMsGroupTagName', undefined);
      storefrontCheckoutForm?.clearErrors('wAutopilotMsTenantId');
    }
  };

  const tenantOptions: MicrosoftTenantOption[] = microsoftTenants.results.map(
    tenant => ({
      label: tenant.name,
      value: tenant.id,
    })
  );

  return (
    <ConfigurationDetailPane stack>
      <Flex alignItems="flex-start" columnGap={spaces.space_m} width="100%">
        <Icon
          icon="logo-windows"
          size="large"
          intent={isDisabled ? 'disabled' : undefined}
        />

        {/* Title and description */}
        <Flex vertical flexGrow="1">
          <Text
            variant="label-large"
            intent={isDisabled ? 'disabled' : undefined}>
            {t('wautopilot.wautopilotSwitch.title')}
          </Text>
          <Flex mt={spaces.space_2xs_rem} alignItems="center">
            <Text variant="body" intent={isDisabled ? 'disabled' : undefined}>
              {t('wautopilot.wautopilotSwitch.description')}
            </Text>
          </Flex>

          <Flex
            mt={spaces.space_s_rem}
            alignItems="center"
            columnGap={spaces.space_xs_rem}>
            <Icon
              icon="truck"
              size="xsmall"
              intent={isDisabled ? 'disabled' : undefined}
            />
            <Text variant="legal" intent={isDisabled ? 'disabled' : undefined}>
              {t('wautopilot.wautopilotSwitch.deliveryNote')}
            </Text>
          </Flex>

          {isDisabled && (
            <Box mt={spaces.space_m} alignSelf="flex-start">
              <Chip
                id="wautopilot-disabled-chip"
                icon="information-circle"
                textColor={chipTextColor}
                backgroundColor={chipBgColor}
                variant="small">
                {t('wautopilot.wautopilotSwitch.disabledChip')}
              </Chip>
            </Box>
          )}
        </Flex>

        <Switch
          id="wautopilot-enabled-switch"
          checked={wAutopilotEnabled ?? false}
          onChange={handleToggle}
          disabled={isDisabled}
        />
      </Flex>

      {wAutopilotEnabled === false && !isDisabled && (
        <Box mt={spaces.space_m}>
          <Banner
            id="wautopilot-opt-out-banner"
            isFlat
            isFullWidth
            intent="warning"
            message={() => (
              <Text variant="body-2">
                <Text variant="body-2" fontWeight="bold" display="inline">
                  {t('wautopilot.wautopilotSwitch.optOutBanner.important')}{' '}
                </Text>
                {t('wautopilot.wautopilotSwitch.optOutBanner.message')}
              </Text>
            )}
          />
        </Box>
      )}

      {wAutopilotEnabled && storefrontCheckoutForm && (
        <Box mt={spaces.space_m_rem} pl={spaces.space_xl_rem}>
          <WAutopilotSubFormContainer>
            <TwoColumnGrid>
              <SelectField
                id="wautopilot-ms-tenant-select"
                name="wAutopilotMsTenantId"
                label={t('wautopilot.wautopilotSwitch.tenantLabel')}
                control={storefrontCheckoutForm.control}
                options={tenantOptions}
                helperText={t('wautopilot.wautopilotSwitch.tenantHelperText')}
                required
                isClearable
              />
              <TextInputField
                id="wautopilot-ms-group-tag-input"
                name="wAutopilotMsGroupTagName"
                label={t('wautopilot.wautopilotSwitch.groupTagLabel')}
                control={storefrontCheckoutForm.control}
                placeholder={t(
                  'wautopilot.wautopilotSwitch.groupTagPlaceholder'
                )}
                helperText={t('wautopilot.wautopilotSwitch.groupTagHelperText')}
              />
            </TwoColumnGrid>
          </WAutopilotSubFormContainer>
        </Box>
      )}
    </ConfigurationDetailPane>
  );
};
